/**
 * AnimatedSplash constants — colors, sizing, timings, and the bundled brand logo.
 * Values mirror the "Stomp" splash concept (Frame 02). The logo is bundled (copied from
 * the CDN logo.webp into assets/Splash) so the launch screen paints instantly with no network.
 */

import type { ImageSourcePropType } from 'react-native';

import { SPLASH_LOGO_DATA_URI } from './splashLogo.data';

/**
 * Inlined data URI (not require/CDN) so the logo always paints — instant, offline-safe, and
 * immune to the iOS-release bundled-require resolution bug. See splashLogo.data.ts.
 */
export const SPLASH_LOGO_SOURCE: ImageSourcePropType = { uri: SPLASH_LOGO_DATA_URI };

/**
 * Element colors. BACKGROUND is the flat brand purple sampled from the app-icon tile
 * (#6C33B4) so the falling logo's own square background blends into the screen — only the
 * mascot reads as dropping in, not a tile. Keep it in sync with the native splash color in
 * app.config.js (the native screen hands off straight into this surface).
 */
export const SPLASH_COLORS = {
  BACKGROUND: '#6C34B4',
  WORDMARK: '#FFFFFF',
  RING: '#FCD34D',
  STREAK: '#FFFFFF',
} as const;

export const SPLASH_LOGO_SIZE = 156;

/** How far above its resting spot the logo starts before it drops. */
export const SPLASH_DROP_DISTANCE = 300;

/**
 * Timeline (ms). Total visible ≈ drop + impact + word + hold + fade ≈ 1.5 s
 * (drop 360 + impact ~420 + word 240 + hold 200 + fade 260). The wordmark is still a
 * separate beat AFTER the impact — the key is that impact is now bounded (SQUASH_OUT is a
 * fixed timing, not an unbounded spring), so the text arrives on schedule (~0.8 s) instead
 * of waiting ~1 s for a bouncy spring to fully settle.
 */
export const SPLASH_TIMINGS = {
  DROP: 360,
  SQUASH_IN: 70,
  // Squash recovery — fixed duration (was an unbounded spring that stalled the wordmark).
  SQUASH_OUT: 200,
  RING: 420,
  STREAK: 360,
  SHAKE: 200,
  WORD: 240,
  HOLD: 200,
  FADE_OUT: 260,
} as const;

/**
 * Speed streaks that radiate from the impact point — angle (deg) + how far each is pushed out.
 * Mirrors the fan of lines in the concept.
 */
export const SPLASH_STREAKS: ReadonlyArray<{ rotate: string; offset: number }> = [
  { rotate: '-58deg', offset: 24 },
  { rotate: '-30deg', offset: 30 },
  { rotate: '0deg', offset: 32 },
  { rotate: '30deg', offset: 30 },
  { rotate: '58deg', offset: 24 },
];
